import { SdkContext } from '../internal'
import type {
  OneOnOneAction,
  OneOnOneAdherence,
  OneOnOneDashboardData,
  OneOnOneDashboardPeriod,
  OneOnOneLogInput,
  OneOnOneMeeting,
  OneOnOneNote,
  OneOnOneNoteVisibility,
  OneOnOnePair,
  OneOnOnePairSummary,
  OneOnOneSeries,
  OneOnOneSeriesInput,
  OneOnOneSeriesStatus,
  OneOnOneTopic
} from '@/types/oneOnOnes'

const asList = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-136 1:1. The community comes from X-CommunityId; only the two
 * participants read a series (anyone else: 403/404), and a private note only
 * its author. Owner/admin and indirect leaders read the adherence only.
 */
export class OneOnOnesResource {
  constructor(private ctx: SdkContext) {}

  async listSeries(params: { spaceId?: string; status?: OneOnOneSeriesStatus } = {}): Promise<OneOnOneSeries[]> {
    return asList<OneOnOneSeries>(await this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/series' }, { query: params }))
  }

  /** `permissions.canSeeAdherence` of the series list (owner/admin or whoever leads someone), so the page never probes the adherence for a 403. */
  async canSeeAdherence(spaceId?: string): Promise<boolean> {
    const body = await this.ctx.envelope<{ permissions?: { canSeeAdherence?: boolean } }>({ httpMethod: 'GET', path: '/one-on-ones/series' }, { query: { ...(spaceId ? { spaceId } : {}), status: 'ACTIVE' } })
    return Boolean(body?.permissions?.canSeeAdherence)
  }

  async createSeries(body: OneOnOneSeriesInput): Promise<OneOnOneSeries> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/series', hasBody: true }, { body })
  }

  async getSeries(seriesId: string): Promise<OneOnOneSeries> {
    return this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/series/{seriesId}' }, { seriesId })
  }

  async pause(seriesId: string): Promise<OneOnOneSeries> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/series/{seriesId}/pause' }, { seriesId })
  }

  async resume(seriesId: string): Promise<OneOnOneSeries> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/series/{seriesId}/resume' }, { seriesId })
  }

  async end(seriesId: string): Promise<OneOnOneSeries> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/series/{seriesId}/end' }, { seriesId })
  }

  async createAction(seriesId: string, body: { title: string; ownerId?: string; dueOn?: string | null; meetingId?: string }): Promise<OneOnOneAction> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/series/{seriesId}/actions', hasBody: true }, { seriesId, body })
  }

  async updateAction(actionId: string, body: { title?: string; ownerId?: string; dueOn?: string | null; status?: 'OPEN' | 'DONE' }): Promise<OneOnOneAction> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/one-on-ones/actions/{actionId}', hasBody: true }, { actionId, body })
  }

  async deleteAction(actionId: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/one-on-ones/actions/{actionId}' }, { actionId })
  }

  /** Only an agreement owned by the report; errors of the PDI come back as `developmentPlan.errors.*`. */
  async takeToPdi(actionId: string, spaceId?: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/actions/{actionId}/pdi', hasBody: true }, { actionId, body: spaceId ? { spaceId } : {} })
  }

  /** E2-E6: log a 1:1 with the direct leader or a direct report (403 `notDirectPair` otherwise). */
  async logMeeting(body: OneOnOneLogInput): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings', hasBody: true }, { body })
  }

  /** E9: only who logged it (the other: 403 `logNotYours`). */
  async updateLog(meetingId: string, body: { date?: string; time?: string; durationMinutes?: number }): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/one-on-ones/meetings/{meetingId}', hasBody: true }, { meetingId, body })
  }

  async deleteLog(meetingId: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/one-on-ones/meetings/{meetingId}' }, { meetingId })
  }

  /** E7 "Minhas 1:1s": one row per person (direct leader, direct reports, anyone with history). */
  async listPairs(): Promise<OneOnOnePairSummary[]> {
    return asList<OneOnOnePairSummary>(await this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/pairs' }, {}))
  }

  /** Owner/admin only (403 `pairsTeamOnly`): every pair of the community, read only. */
  async listCommunityPairs(): Promise<OneOnOnePairSummary[]> {
    return asList<OneOnOnePairSummary>(await this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/pairs' }, { query: { all: '1' } }))
  }

  /** With `withId` (owner/admin): the pair of those two people, read only. */
  async getPair(memberId: string, withId?: string | null): Promise<OneOnOnePair> {
    return this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/pairs/{memberId}' }, { memberId, ...(withId ? { query: { with: withId } } : {}) })
  }

  async getMeeting(meetingId: string): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/meetings/{meetingId}' }, { meetingId })
  }

  async reschedule(meetingId: string, scheduledAt: string, durationMinutes?: number): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings/{meetingId}/reschedule', hasBody: true }, { meetingId, body: { scheduledAt, ...(durationMinutes ? { durationMinutes } : {}) } })
  }

  async cancelMeeting(meetingId: string): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings/{meetingId}/cancel' }, { meetingId })
  }

  async completeMeeting(meetingId: string): Promise<OneOnOneMeeting> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings/{meetingId}/done' }, { meetingId })
  }

  async addTopic(meetingId: string, text: string): Promise<OneOnOneTopic> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings/{meetingId}/topics', hasBody: true }, { meetingId, body: { text } })
  }

  async reorderTopics(meetingId: string, topicIds: string[]): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/one-on-ones/meetings/{meetingId}/topics/order', hasBody: true }, { meetingId, body: { topicIds } })
  }

  async updateTopic(topicId: string, body: { discussed?: boolean; text?: string }): Promise<OneOnOneTopic> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/one-on-ones/topics/{topicId}', hasBody: true }, { topicId, body })
  }

  async deleteTopic(topicId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/one-on-ones/topics/{topicId}' }, { topicId })
  }

  async addNote(meetingId: string, body: { visibility: OneOnOneNoteVisibility; body: string }): Promise<OneOnOneNote> {
    return this.ctx.call({ httpMethod: 'POST', path: '/one-on-ones/meetings/{meetingId}/notes', hasBody: true }, { meetingId, body })
  }

  async updateNote(noteId: string, body: { visibility?: OneOnOneNoteVisibility; body?: string }): Promise<OneOnOneNote> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/one-on-ones/notes/{noteId}', hasBody: true }, { noteId, body })
  }

  async deleteNote(noteId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/one-on-ones/notes/{noteId}' }, { noteId })
  }

  /** Owner/admin (the community) and indirect leaders (their subtree); anyone else 403. */
  async adherence(spaceId?: string): Promise<OneOnOneAdherence> {
    return this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/adherence' }, { query: spaceId ? { spaceId } : {} })
  }

  /** The "Gestão" dashboard, aggregated: owner/admin the community, a leader their tree; anyone else 403. */
  async dashboard(params: { period?: OneOnOneDashboardPeriod; leaderId?: string; teamId?: string } = {}): Promise<OneOnOneDashboardData> {
    const query: Record<string, string> = {}
    if (params.period) query.period = params.period
    if (params.leaderId) query.leaderId = params.leaderId
    if (params.teamId) query.teamId = params.teamId
    return this.ctx.call({ httpMethod: 'GET', path: '/one-on-ones/dashboard' }, { query })
  }
}
